import { Component, ElementRef, HostListener, Input, OnInit, ViewChild } from '@angular/core';
import { NgFor, NgIf } from '@angular/common';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { SvgIconComponent } from '../svg-icon/svg-icon.component';

interface AuthLanguage {
  code: string;
  labelKey: string;
}

/**
 * Shell for every unauthenticated page: centered logo bar, a single centered
 * content column, and a footer carrying the copyright line plus the language
 * selector.
 *
 * It exists because that chrome used to be copy-pasted across login,
 * forget-password and reset-password (and reinvented again by the maintenance
 * and access-denied pages), which is how the five surfaces drifted apart.
 * Page-specific content goes in via `<ng-content>`.
 */
@Component({
  selector: 'ui-auth-layout',
  standalone: true,
  imports: [NgIf, NgFor, TranslateModule, SvgIconComponent],
  templateUrl: './auth-layout.component.html',
  styleUrls: ['./auth-layout.component.scss'],
})
export class AuthLayoutComponent implements OnInit {
  /** Hidden on pages where switching language is not meaningful. */
  @Input() showLanguageSelector = true;

  readonly languages: AuthLanguage[] = [
    { code: 'en', labelKey: 'language.english' },
    { code: 'fr', labelKey: 'language.french' },
    { code: 'it', labelKey: 'language.italian' },
  ];

  readonly currentYear = new Date().getFullYear();

  currentLanguage = 'en';
  isDropdownOpen = false;

  @ViewChild('languageSelector') languageSelector?: ElementRef<HTMLElement>;

  constructor(private readonly translateService: TranslateService) {}

  ngOnInit(): void {
    const savedLang = localStorage.getItem('lang') || 'en';
    this.translateService.use(savedLang);
    this.currentLanguage = savedLang;
  }

  toggleDropdown(): void {
    this.isDropdownOpen = !this.isDropdownOpen;
  }

  switchLanguage(lang: string): void {
    if (lang !== this.translateService.currentLang) {
      this.translateService.use(lang);
    }
    localStorage.setItem('lang', lang);
    this.currentLanguage = lang;
    this.isDropdownOpen = false;
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    if (!this.isDropdownOpen) {
      return;
    }
    const host = this.languageSelector?.nativeElement;
    if (host && !host.contains(event.target as Node)) {
      this.isDropdownOpen = false;
    }
  }

  @HostListener('document:keydown.escape')
  onEscape(): void {
    this.isDropdownOpen = false;
  }
}
